import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import {css} from 'aphrodite';
import Markdown from 'components/Markdown';
import DropdownSelector from 'components/DropdownSelector';

const REQUEST_REP_PATTERN = /request|rep|contact/i;
const isRequestRepLink = (name, href) =>
  REQUEST_REP_PATTERN.test(name || '') || REQUEST_REP_PATTERN.test(href || '');

const Header = (props) => {
  const {
    settings,
    styleSheet,
    authenticated,
    logoAssets,
    handleLogout,
    expandBoxedIsi
  } = props;
  const productLogo = <img src={logoAssets[settings.header.productLogo]} className={css(styleSheet.headerLogo)} alt={settings.content.productLogoAlt} />;

  const numLinks = settings.header.links.length;
  const isFloatRight = settings.styles?.headerLink?.float === 'right';
  const headerLinks = settings.header.links.map((link, index) => {
    const trackAttrs = isRequestRepLink(link.name, link.href)
      ? {'data-track': 'request-rep', 'data-track-label': link.name}
      : {};

    // accessibility tab order should be from left to right, but DOM order is reversed when style is floating right
    // rely on default tab ordering of links if floating right isn't set
    const tabIndex = isFloatRight ? numLinks - index : undefined;
    // Relative links should be routed within reactrouter
    if (link.href.charAt(0) === '/') {
      return <Link
        key={link.name}
        to={link.href}
        className={css(styleSheet.headerLink, styleSheet.headerPageLink)}
        target={link.newTab ? '_blank' : null}
        rel='noopener noreferrer'
        tabIndex={tabIndex}
        aria-label={link.newTab ? `${link.name} (opens in new tab)` : undefined}
        {...trackAttrs}
      >{link.name}</Link>;
    }

    if (!settings.isi.boxedIsi.disabled && link.href === '#isi') {
      return <a key={link.name} className={css(styleSheet.headerLink)} href={link.href} tabIndex={tabIndex} onClick={expandBoxedIsi} {...trackAttrs}>{link.name}</a>;
    }

    return <a key={link.name} className={css(styleSheet.headerLink)} href={link.href} tabIndex={tabIndex} target={link.newTab ? '_blank' : null} rel='noopener noreferrer' aria-label={link.newTab ? `${link.name} (opens in new tab)` : undefined} {...trackAttrs}>{link.name}</a>;
  });

  const linkSectionGridWidth = 12 - settings.header.logoSectionGridWidth;

  return (
    <div>
      {!settings.header.headerBar.disabled && settings.header.headerBar.markdown &&
        <div className={css(styleSheet.headerBar)}>
          <Markdown>
            {settings.header.headerBar.markdown}
          </Markdown>
        </div>
      }
      <header data-component="Header" className={css(styleSheet.header)}>
        <Container>
          <Row>
            <Col xs='12' sm={settings.header.logoSectionGridWidth}>
              {settings.header.productLogo &&
                (settings.header.productLogoLink.charAt(0) === '/' ?
                  <Link to={settings.header.productLogoLink} rel='noopener noreferrer'>{productLogo}</Link>
                  :
                  <a href={settings.header.productLogoLink} rel='noopener noreferrer'>{productLogo}</a>)
              }
            </Col>
            <Col xs='12' sm={linkSectionGridWidth} className='d-flex flex-column'>
              <nav aria-label="Primary">
                {authenticated &&
                  <a
                    className={css(styleSheet.headerLink)}
                    tabIndex={isFloatRight ? numLinks + 1 : 0}
                    role='button'
                    onClick={handleLogout}
                    onKeyDown={(e) => ((e.key === 'Enter') && handleLogout())}
                  >
                    Logout
                  </a>
                }
                {headerLinks}
                {settings.header.dropdown && settings.header.dropdown.enabled &&
                <div>
                  <label htmlFor="linkOutSelector" className={css(styleSheet.linkOutLabel)}>Go to Product:</label>
                  <DropdownSelector id="linkOutSelector"
                    menuItems={settings.header.dropdown.menuItems}
                    placeholderText={settings.header.dropdown.placeholderText}
                    buttonLabel={settings.header.dropdown.buttonLabel}
                    newTab={settings.header.dropdown.newTab}
                    dropdownContainerStyle={'pull-right'}
                    dropdownSelectorStyle={css(styleSheet.dropdownSelectorDropdown)}
                    dropdownToggleStyle={css(styleSheet.dropdownSelectorToggle)}
                    gobuttonStyle={css(styleSheet.dropdownSelectorButton)}
                  />
                </div>}
              </nav>
              <div className={css(styleSheet.headerUsageDisclaimer)}>
                <Markdown>
                  {settings.content.headerUsageDisclaimer}
                </Markdown>
              </div>
            </Col>
          </Row>
        </Container>
      </header>
    </div>
  );
};

Header.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  logoAssets: PropTypes.object.isRequired,
  authenticated: PropTypes.bool,
  handleLogout: PropTypes.func.isRequired,
  expandBoxedIsi: PropTypes.func.isRequired,
};

export default Header;
